import {
  AfterViewInit,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ColumnConfig,
  CustomAction,
  FieldType,
  GenericTableComponent,
  GlobalAction,
  HeaderConfigurationService,
  ModalFieldConfig,
  ModalMode,
  MoreDataConfig,
  TableAction,
  TableActionConfig,
} from '@solcre-org/core-ui';
import { environment } from '../../../../environments/environment.local';
import { VoyageModel } from '../../../shared/models/voyage.model';
import { VesselModel } from '../../../shared/models/vessel.model';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { vesselUriTemplate } from '../../../shared/templates/vesselUriTemplate';
import { DataStoreService } from '../../../shared/services/data-store.service';
import { DateUtils } from '../../../shared/utils/date.utils';
import { Router } from '@angular/router';
import { EIGHT_DIGITS_PATTERN_VALIDATOR } from '../../../shared/validators/pattern-validators';

@Component({
  selector: 'app-voyages',
  imports: [GenericTableComponent, TranslateModule],
  templateUrl: './voyages.html'
})
export class Voyages implements OnInit, AfterViewInit {
  //Services
  private router: Router = inject(Router);
  private headerConfigurationService = inject(HeaderConfigurationService);
  private translateService: TranslateService = inject(TranslateService);
  private dataStoreService: DataStoreService = inject(DataStoreService);

  //Models
  voyages = signal<VoyageModel[]>([]);
  private readonly duplicateVoyageNumber = signal<string | null>(null);
  private readonly voyageForm = signal<FormGroup | null>(null);

  //Custom templates for special columns
  @ViewChild('revenueTemplate', { static: true }) revenueTemplate!: TemplateRef<{
    $implicit: VoyageModel;
    column: ColumnConfig<VoyageModel>;
  }>;

  //Configs
  endpoint = environment.API.VOYAGES_URI;
  columns: ColumnConfig<VoyageModel>[] = [];
  modalFields = computed<ModalFieldConfig<VoyageModel>[]>(() => {
    // Force core-ui modal field re-validation when API duplicate state changes.
    this.duplicateVoyageNumber();

    return [
      {
        key: 'voyageNumber',
        label: 'voyages.voyageNumber',
        placeholder: this.translateService.instant('voyages.voyageNumberPlaceholder'),
        type: FieldType.TEXT,
        readonly: false,
        validators: [Validators.maxLength(10), this.duplicateVoyageNumberValidator],
        errorMessages: {
          maxlength: this.translateService.instant('validators.maxLength', { maxLength: 10 }),
          duplicateVoyageNumber: this.translateService.instant('voyages.errors.duplicateVoyageNumber'),
        } as { [key: string]: string },
      },
      {
        key: 'vessel',
        label: 'voyages.vessel',
        placeholder: this.translateService.instant('voyages.vesselPlaceholder'),
        type: FieldType.SELECT,
        searchable: true,
        readonly: false,
        defaultValue: '',
        options: this.dataStoreService
          .selectAll<VesselModel>('vessels')()
          .map((vessel: VesselModel) => ({
            value: vessel.id,
            label: vessel.vesselName ?? '---',
          })),
        validators: [Validators.required],
        errorMessages: {
          required: this.translateService.instant('validators.required'),
        } as { [key: string]: string },
        customViewTemplate: (vesselId: string) => {
          return this.vesselName(vesselId);
        },
      },
      {
        key: 'plannedDepartureDate',
        label: 'voyages.plannedDepartureDate',
        type: FieldType.DATE,
        readonly: false,
        validators: [Validators.required],
        errorMessages: {
          required: this.translateService.instant('validators.required'),
        } as { [key: string]: string },
      },
      {
        key: 'vlsfoUsdPerMetricTon',
        label: this.translateService.instant('voyages.vlsfo'),
        placeholder: this.translateService.instant('voyages.vlsfoPlaceholder'),
        readonly: false,
        type: FieldType.TEXT,
        validators: (formValue: VoyageModel) => !formValue?.mgoUsdPerMetricTon ? [Validators.required, EIGHT_DIGITS_PATTERN_VALIDATOR] : [EIGHT_DIGITS_PATTERN_VALIDATOR],
        errorMessages: {
          required: this.translateService.instant('validators.required'),
          pattern: this.translateService.instant('validators.digitsdecimalPattern', { digits: 8, decimals: 2 }),
        }
      },
      {
        key: 'mgoUsdPerMetricTon',
        label: this.translateService.instant('voyages.mgo'),
        placeholder: this.translateService.instant('voyages.mgoPlaceholder'),
        readonly: false,
        type: FieldType.TEXT,
        validators: (formValue: VoyageModel) => !formValue?.vlsfoUsdPerMetricTon ? [Validators.required, EIGHT_DIGITS_PATTERN_VALIDATOR] : [EIGHT_DIGITS_PATTERN_VALIDATOR],
        errorMessages: {
          required: this.translateService.instant('validators.required'),
          pattern: this.translateService.instant('validators.digitsdecimalPattern', { digits: 8, decimals: 2 }),
        }
      },
    ];
  });
  globalActions: GlobalAction<VoyageModel>[] = [];
  actions: TableActionConfig[] = [
    { action: TableAction.EDIT, isExtra: false },
    { action: TableAction.DELETE, isExtra: true },
    { action: TableAction.CREATE, isExtra: false },
  ];
  configCustomActions: CustomAction<VoyageModel>[] = [
    {
      title: this.translateService.instant('voyages.showAction'),
      icon: 'icon-arrow-right',
      isExtra: false,
      tooltipPosition: 'top',
      callback: (item: VoyageModel) => {
        this.router.navigate(['app', 'voyages', item.getId()]);
      },
    },
  ];
  moreData: MoreDataConfig<any>[] = [
    {
      key: 'vessels',
      endpoint: environment.API.VESSELS_URI,
      model: VesselModel,
    },
  ];

  //Events
  ngOnInit() {
    this.headerConfigurationService.setEndpointHeaders(this.endpoint, {
      patch: [{ 'Content-Type': 'application/merge-patch+json' }],
    });
  }

  ngAfterViewInit(): void {
    this.columns = [
      {
        label: 'voyages.voyageNumber',
        key: 'voyageNumber',
      },
      {
        label: 'voyages.vessel',
        key: 'vessel',
        format: (vesselId: string) => {
          return this.vesselName(vesselId);
        },
      },
      {
        label: 'voyages.plannedDepartureDate',
        key: 'plannedDepartureDate',
        format: (value: any) => DateUtils.formatDateOnly(value),
      },
      {
        label: 'voyages.vlsfo',
        key: 'vlsfoUsdPerMetricTon',
        format: (value: number) => value ? '$' + Number(value).toFixed(2) : '$ 0.00',
      },
      {
        label: 'voyages.mgo',
        key: 'mgoUsdPerMetricTon',
        format: (value: number) => value ? '$' + Number(value).toFixed(2) : '$ 0.00',
      },
      {
        label: 'voyages.revenue',
        key: 'revenue',
        template: this.revenueTemplate,
      },
    ];
  }

  voyagesModelFactory = (data: Partial<VoyageModel>): VoyageModel => {
    return new VoyageModel().fromJSON(data);
  };

  onVoyageFormReady(form: FormGroup): void {
    this.voyageForm.set(form);
    this.setDuplicateVoyageNumber(null);
  }

  onVoyageModalData(voyage: VoyageModel | null): void {
    if (!this.duplicateVoyageNumber()) {
      return;
    }

    if (this.normalizeVoyageNumber(voyage?.voyageNumber) !== this.duplicateVoyageNumber()) {
      this.setDuplicateVoyageNumber(null);
    }
  }

  onApiError = (error: any, defaultMessage: string, operation: string): string => {
    const errObj = error?.error || error;

    if (
      (operation === 'create' || operation === 'update') &&
      errObj?.status === 422 &&
      Array.isArray(errObj?.violations)
    ) {
      const duplicateViolation = errObj.violations.find((violation: any) =>
        this.isVoyageNumberDuplicateViolation(violation)
      );

      if (duplicateViolation) {
        this.setDuplicateVoyageNumber(this.getCurrentVoyageNumberValue());
        return this.translateService.instant('voyages.errors.duplicateVoyageNumber');
      }
    }

    return defaultMessage;
  };

  vesselName(vesselId: string): string {
    return (
      this.dataStoreService.getById<VesselModel>('vessels', vesselId)
        ?.vesselName ?? ''
    );
  }

  onMoreDataLoaded(event: Map<string, any[]>): void {
    if (!event) {
      return;
    }

    const vesselsData = event.get('vessels');
    if (Array.isArray(vesselsData)) {
      this.dataStoreService.hydrate<VesselModel>(
        'vessels',
        vesselsData.map((vessel: VesselModel) => {
          //TODO: Ver como resolver de convivir id y URI
          vessel.id = vesselUriTemplate(vessel.id);
          return vessel;
        })
      );
    }
  }

  private readonly duplicateVoyageNumberValidator: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const duplicateVoyageNumber = this.duplicateVoyageNumber();
    const currentValue = this.normalizeVoyageNumber(control.value);

    if (!duplicateVoyageNumber || !currentValue) {
      return null;
    }

    return duplicateVoyageNumber === currentValue
      ? { duplicateVoyageNumber: true }
      : null;
  };

  private setDuplicateVoyageNumber(value: string | null): void {
    this.duplicateVoyageNumber.set(value);
    this.voyageForm()?.get?.('voyageNumber')?.updateValueAndValidity({ emitEvent: false });
  }

  private getCurrentVoyageNumberValue(): string | null {
    return this.normalizeVoyageNumber(this.voyageForm()?.get?.('voyageNumber')?.value);
  }

  private normalizeVoyageNumber(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const normalizedValue = value.trim();
    return normalizedValue.length > 0 ? normalizedValue : null;
  }

  private isVoyageNumberDuplicateViolation(violation: any): boolean {
    const propertyPath = String(
      violation?.propertyPath ?? violation?.property ?? violation?.field ?? ''
    ).toLowerCase();
    const message = String(violation?.message ?? '').toLowerCase();

    return (
      propertyPath === 'number' ||
      propertyPath === 'voyagenumber' ||
      propertyPath.endsWith('.number') ||
      propertyPath.endsWith('.voyagenumber') ||
      (message.includes('voyage number') && message.includes('already exists'))
    );
  }
}
